import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';

import { DESIGN_MONTH_GATE_FLAG } from '#components/feature-flag/flags.js';
import { useFeatureFlag } from '#components/feature-flag/use-feature-flag.js';
import { Toast } from '#components/toast/toast.js';
import { useTRPC } from '#src/trpc.js';
import { useAbilityContext } from '#state/contexts/ability-context.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { captureAnalyticsEvent } from '#utils/analytics/analytics-events.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { generatedRevisionName } from '../revision-name.js';
import { NewProjectDesignMonthGate } from './design-month-gate/new-project-design-month-gate.js';
import { shouldShowDesignMonthGate } from './design-month-gate/should-show-design-month-gate.js';
import { PickProjectFormatStep } from './steps/pick-project-format-step.js';
import { PROVISIONAL_PROJECT_NAME } from './utils/derive-project-name.js';

import type { GetNewProjectDesignMonthGateOutputDto } from '@cadstrom/backend/shared';
import type { ProjectFormat } from './utils/project-format.js';

export const Route = createFileRoute('/_authorization-gate/orgs/$orgName/projects/new/upload')({
	staticData: {
		authorize: (ability) => ability.can('manage', 'project'),
	},
	head: () => ({ meta: [{ title: 'New project • Cadstrom' }] }),
	component: NewProjectUploadPage,
});

/**
 * Entry point for creating a project without naming it first: the user picks the CAD tool
 * their design came from, and only then is a project created — provisionally named, and
 * renamed once the uploaded files identify it.
 *
 * Creation is deferred to this point rather than to the first file drop because the
 * uploader stamps every file with the project and revision it belongs to, so both must
 * exist before any bytes move.
 *
 * An organization with no room for another design review project is stopped here by the
 * design-month gate, and so is one whose room cannot be read.
 */
function NewProjectUploadPage() {
	const { orgName } = Route.useParams();
	const navigate = Route.useNavigate();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const addToast = useAddToast();
	const el = useDataElTagGenerator(NewProjectUploadPage);

	const ability = useAbilityContext();
	const designMonthGateFlag = useFeatureFlag(DESIGN_MONTH_GATE_FLAG);
	const isStaff = ability.can('manage', 'devTools');

	const [format, setFormat] = useState<ProjectFormat | null>(null);
	const [isCheckingGate, setIsCheckingGate] = useState(false);
	const creatingRef = useRef(false);

	const { data: orgData } = useQuery(trpc.organization.get.queryOptions({ organizationName: orgName }));
	const orgId = orgData?.organization.id ?? null;

	const checksGate = designMonthGateFlag === true && !isStaff;
	const gateOptions = trpc.billing.getNewProjectDesignMonthGate.queryOptions({ organizationId: orgId ?? '' });
	const { data: gate } = useQuery({ ...gateOptions, enabled: checksGate && orgId !== null, throwOnError: false });

	/**
	 * Re-reads the organization's room for a new project, so a hold taken since the page loaded
	 * is counted. Any failure counts as unreadable.
	 *
	 * @returns Whether a new project may be created.
	 */
	const mayCreateProject = async (): Promise<boolean> => {
		let fresh: GetNewProjectDesignMonthGateOutputDto | null;
		try {
			fresh = await queryClient.fetchQuery({ ...gateOptions, staleTime: 0, retry: false });
		} catch {
			fresh = null;
		}
		if (fresh === null || fresh.balanceUnavailable) {
			addToast(
				<Toast severity='error' title="Couldn't check your account">
					<span>
						We couldn&apos;t confirm your account&apos;s available design review projects. Please try again in a few
						minutes.
					</span>
				</Toast>,
			);
			return false;
		}
		return !fresh.gated;
	};

	const { mutateAsync: createProject, isPending } = useMutation(
		trpc.project.createWithBuild.mutationOptions({
			onSuccess: () => {
				void queryClient.invalidateQueries({ queryKey: trpc.project.list.queryKey() });
			},
		}),
	);

	const handleContinue = async () => {
		if (format === null || orgId === null || creatingRef.current) {
			return;
		}
		creatingRef.current = true;

		const revisionName = generatedRevisionName();

		try {
			if (checksGate) {
				setIsCheckingGate(true);
				const allowed = await mayCreateProject();
				setIsCheckingGate(false);
				if (!allowed) {
					return;
				}
			}

			const result = await createProject({
				name: PROVISIONAL_PROJECT_NAME,
				ownerId: orgId,
				revisionName,
				ensureUniqueName: true,
			});

			captureAnalyticsEvent('project_created', {
				owner_id: orgId,
				project_name: result.project.name,
				revision_name: revisionName,
			});

			await navigate({
				to: '/orgs/$orgName/projects/$projectId/revisions/$revisionId/upload',
				params: { orgName, projectId: result.project.id, revisionId: result.buildId },
				search: { tool: format },
			});
		} catch (error: unknown) {
			console.error('[NewProjectUploadPage]_FailedToCreateProject', error);
			const message = error instanceof Error ? error.message : 'Failed to create project';
			captureAnalyticsEvent('project_created_error', { owner_id: orgId, error_message: message });
			addToast(
				<Toast severity='error' title='Could not create project'>
					<span>{message}</span>
				</Toast>,
			);
		} finally {
			creatingRef.current = false;
		}
	};

	return (
		<div {...el()} className='flex min-h-0 grow basis-0 flex-col items-center p-2 pt-0'>
			<div {...el('column')} className='flex min-h-0 w-full max-w-3xl grow basis-0 flex-col gap-2'>
				<div {...el('header')} className='flex shrink-0 items-center justify-between'>
					<h1 className='mx-0.5 text-lg font-semibold'>New project</h1>
				</div>

				<PickProjectFormatStep
					value={format}
					onChange={setFormat}
					onContinue={() => void handleContinue()}
					isContinuing={isPending || isCheckingGate}
					disabled={orgId === null}
				/>
			</div>

			{orgId !== null && (
				<NewProjectDesignMonthGate
					open={shouldShowDesignMonthGate({ flagEnabled: designMonthGateFlag, isStaff, gated: gate?.gated })}
					organizationId={orgId}
					onClose={() => void navigate({ to: '/orgs/$orgName/projects', params: { orgName } })}
				/>
			)}
		</div>
	);
}
